import { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { DynamicUrlCategory } from 'constants/dynamicUrlCategory';
import { PropertyTypeIds } from 'constants/propertyTypes';
import { SortDirections } from 'constants/sortDirections';
import { authContext } from 'contexts/AuthContext';
import { IMarket, ISubmarket } from 'interfaces/IMarket';
import { IDynamicURL } from 'interfaces/IDynamicURL';
import { IPropertySubType } from 'interfaces/IPropertySubType';
import { IPropertyClasses } from 'interfaces/IPropertyClasses';
import { IPropertyTypeResponse } from 'interfaces/IPropertyType';
import { SUB_MARKETS_QUERY } from 'graphql/markets';
import { DYNAMIC_URLS } from 'graphql/dynamicUrl';
import {
  PROPERTY_CLASSES_QUERY,
  PROPERTY_SUBTYPES_QUERY,
  PROPERTY_TYPES_ENTITIES_QUERY,
} from 'graphql/property';
import { ActivitiesSearchService } from 'services/ActivitiesSearchService';
import locations from 'routes';
import { getDefaultPreferencesFilter } from 'utils/preferences';

interface Props {
  isIndustrialFlex: boolean;
  userMarkets: IMarket[];
  currentMarket: IMarket | undefined;
  setCurrentMarket: (market: IMarket) => void;
}

export const useNavBarData = ({
  isIndustrialFlex,
  userMarkets,
  currentMarket,
  setCurrentMarket,
}: Props) => {
  const { user } = useContext(authContext);
  const history = useHistory();

  /** Markets menu**/

  const { data: submarketsData, loading: submarketsDataLoading } = useQuery<{
    submarkets: ISubmarket[];
  }>(SUB_MARKETS_QUERY, {
    variables: {
      search: {
        filter: {
          markets: [{ id: currentMarket?.id }],
          propertyTypeIds: [PropertyTypeIds.office, PropertyTypeIds.industrial],
        },
        order: { field: 'name', direction: SortDirections.asc },
      },
    },

    skip: !currentMarket,
  });

  /** Data menu**/
  const {
    data: propertyTypesData,
    loading: propertyTypesDataLoading,
  } = useQuery<IPropertyTypeResponse>(PROPERTY_TYPES_ENTITIES_QUERY);

  const { data: subTypesData, loading: subTypesDataLoading } = useQuery<{
    propertySubtypes: IPropertySubType[];
  }>(PROPERTY_SUBTYPES_QUERY);

  const { data: classesData, loading: classesDataLoading } = useQuery<{
    propertyClasses: IPropertyClasses[];
  }>(PROPERTY_CLASSES_QUERY, { skip: isIndustrialFlex });

  /** Analytics menu**/
  const { data: dynamicUrlsData, loading: dynamicUrlsDataLoading } = useQuery<{
    dynamicURLs: IDynamicURL[];
  }>(DYNAMIC_URLS, {
    variables: {
      search: {
        categories: DynamicUrlCategory.ANALYTICSDASHBOARDS,
      },
    },
  });

  /** Handlers **/
  const handleHoverMarket = (market: IMarket) => {
    if (market.id !== currentMarket?.id) {
      setCurrentMarket(market);
    }
  };

  const handleClickMarket = (market: IMarket) => {
    history.push(locations.showMarket(market.id));
  };

  const onNewsClickHandle = () => {
    return locations.exploreActivity(
      ActivitiesSearchService.parseCustomCriteriaToUrlParams({
        customCriteria: {
          ...getDefaultPreferencesFilter(userMarkets ?? [], user),
        },
      }),
    );
  };

  return {
    data: {
      analytics: dynamicUrlsData?.dynamicURLs ?? [],
      subTypes: subTypesData?.propertySubtypes ?? [],
      classes: classesData?.propertyClasses ?? [],
      propertyTypes: propertyTypesData?.propertyTypesEntities ?? [],
      submarkets: submarketsData?.submarkets ?? [],
      currentMarket,
    },
    loading: {
      dynamicUrlsDataLoading,
      propertyTypesDataLoading,
      subTypesDataLoading,
      classesDataLoading,
      submarketsDataLoading,
    },
    handlers: {
      handleHoverMarket,
      handleClickMarket,
      onNewsClickHandle,
    },
  };
};
